/**
 * api.ts — 前端 API 客户端
 *
 * 策略：
 * 1. 优先请求后端 Workers API（VITE_API_BASE_URL，留空则走 Vite 代理 /api）；
 * 2. 请求失败或后端不可用时，自动降级到前端种子数据（seed.ts），
 *    保证「无外部服务也能正常浏览首页」。
 */
import {
  seedPrompts,
  seedCategories,
  type Prompt,
  type Category,
} from '../data/seed';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

let warned = false;

/** 拼接完整 API 地址 */
function apiUrl(path: string): string {
  return API_BASE ? `${API_BASE}${path}` : `/api${path}`;
}

/** 输出一次性的降级提示，避免刷屏 */
function warnFallback(scope: string): void {
  if (!warned) {
    console.warn(`[PromptHub] 后端 API 不可用，使用前端种子数据兜底（${scope}）。`);
    warned = true;
  }
}

/** 通用 JSON 请求 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), {
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface PromptQuery {
  category?: string;
  q?: string;
  sort?: 'popular' | 'latest';
  limit?: number;
}

/** 本地过滤种子数据，模拟后端查询 */
function filterSeed(query: PromptQuery): Prompt[] {
  let list = [...seedPrompts];
  if (query.category) {
    list = list.filter((p) => p.category === query.category);
  }
  if (query.q) {
    const kw = query.q.toLowerCase();
    list = list.filter(
      (p) => p.title.toLowerCase().includes(kw) || p.content.toLowerCase().includes(kw),
    );
  }
  if (query.sort === 'popular') {
    list.sort((a, b) => b.likes - a.likes);
  } else {
    list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  return list.slice(0, query.limit ?? 50);
}

/** 获取 Prompts 列表（支持分类 / 搜索 / 排序） */
export async function getPrompts(query: PromptQuery = {}): Promise<Prompt[]> {
  const params = new URLSearchParams();
  if (query.category) params.set('category', query.category);
  if (query.q) params.set('q', query.q);
  if (query.sort) params.set('sort', query.sort);
  if (query.limit) params.set('limit', String(query.limit));
  try {
    const res = await request<{ data: Prompt[] }>(`/prompts?${params.toString()}`);
    return res.data ?? [];
  } catch {
    warnFallback('prompts');
    return filterSeed(query);
  }
}

/** 获取单个 Prompt 详情 */
export async function getPrompt(id: string): Promise<Prompt | null> {
  try {
    const res = await request<{ data: Prompt }>(`/prompts/${id}`);
    return res.data ?? null;
  } catch {
    warnFallback('prompt detail');
    return seedPrompts.find((p) => p.id === id) ?? null;
  }
}

/** 获取分类列表 */
export async function getCategories(): Promise<Category[]> {
  try {
    const res = await request<{ data: Category[] }>('/categories');
    return res.data ?? [];
  } catch {
    warnFallback('categories');
    return seedCategories;
  }
}

export interface NewPromptInput {
  title: string;
  content: string;
  category: string;
  author: string;
  coverUrl?: string;
}

/** 提交新 Prompt（需要后端在线，无兜底） */
export async function createPrompt(input: NewPromptInput): Promise<Prompt> {
  const res = await request<{ data: Prompt }>('/prompts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.data;
}

export interface SubscribeResult {
  email: string;
  alreadySubscribed?: boolean;
  emailSent?: boolean;
}

/** 邮箱订阅（需要后端在线，无兜底） */
export async function subscribe(email: string): Promise<SubscribeResult> {
  const res = await request<{ data: SubscribeResult }>('/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return res.data;
}

export interface UploadResult {
  key: string;
  url: string;
}

/** 上传图片到 R2（需要后端在线，无兜底） */
export async function uploadImage(file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(apiUrl('/upload'), { method: 'POST', body: form });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? '上传失败');
  }
  const json = (await res.json()) as { data: UploadResult };
  return json.data;
}
